import { useParams, Navigate } from 'react-router-dom'
import { useDevModeStore } from '../../stores/devModeStore'
import type { GeneratorType } from '@creanizer/types'
import { NameGenerator } from './components/NameGenerator'
import { PlotGenerator } from './components/PlotGenerator'
import { TitleGenerator } from './components/TitleGenerator'
import { CharacterGenerator } from './components/CharacterGenerator'
import { RelationshipGenerator } from './components/RelationshipGenerator'
import { WikipediaGenerator } from './components/WikipediaGenerator'
import { ImagePromptGenerator } from './components/ImagePromptGenerator'
import { EnemyGenerator } from './components/EnemyGenerator'
import { ItemGenerator } from './components/ItemGenerator'
import { WorldBuilder } from './components/WorldBuilder'
import { SceneGenerator } from './components/SceneGenerator'
import { SkillGenerator } from './components/SkillGenerator'
import { LyricGenerator } from './components/LyricGenerator'
import { DungeonGenerator } from './components/DungeonGenerator'
import { CommunityGenerator } from './components/CommunityGenerator'
import { SocietyGenerator } from './components/SocietyGenerator'
import { BuildingGenerator } from './components/BuildingGenerator'
import { LocationGenerator } from './components/LocationGenerator'
import { DocumentGenerator } from './components/DocumentGenerator'
import { SummaryGenerator } from './components/SummaryGenerator'
import { ConsultationTool } from './components/ConsultationTool'
import { ScenarioGenerator } from './components/ScenarioGenerator'
import DialogueSimulator from './components/DialogueSimulator'
import { EventGenerator } from './components/EventGenerator'
import TextGameTool from './components/TextGameToolV2'
import { SunoPromptGenerator } from './components/SunoPromptGenerator'
import {
  GeneratorModelProvider,
  GeneratorModelSelector,
} from './components/GeneratorModelSelector'

// 既存のジェネレータコンポーネント
const generatorComponents: Partial<Record<GeneratorType | string, React.ComponentType>> = {
  name: NameGenerator,
  plot: PlotGenerator,
  title: TitleGenerator,
  character: CharacterGenerator,
  relationship: RelationshipGenerator,
  wikipedia: WikipediaGenerator,
  imagePrompt: ImagePromptGenerator,
  enemy: EnemyGenerator,
  item: ItemGenerator,
  worldbuilder: WorldBuilder,
  skill: SkillGenerator,
  scene: SceneGenerator,  // 公開
  community: CommunityGenerator,  // 公開
  dungeon: DungeonGenerator,  // 公開
  location: LocationGenerator,  // 公開
  society: SocietyGenerator,  // 公開
  building: BuildingGenerator,  // 公開
  summary: SummaryGenerator,  // 公開
  document: DocumentGenerator,  // 正式リリース
  consultation: ConsultationTool,  // 正式リリース（相談ツール）
  dialogue: DialogueSimulator,  // 正式リリース（対話シミュレータ）
  suno: SunoPromptGenerator,  // 正式リリース（Sunoプロンプトジェネレータ）
}

// 開発中のジェネレータコンポーネント（実装済みのものはここに追加）
const devGeneratorComponents: Record<string, React.ComponentType> = {
  lyric: LyricGenerator,  // 実装済み
  scenario: ScenarioGenerator,  // 実装済み（シナリオジェネレータ）
  event: EventGenerator,  // 実装済み（イベントジェネレータ）
  textgame: TextGameTool,  // 実装済み（テキストゲームツール）
  // リリース時の手順：
  // 1. この行を削除
  // 2. generatorComponents に コンポーネントを追加
  // 3. devGeneratorTypes から タイプを削除
  // 4. HomePage.tsx で devOnly: true を削除
}

// 開発中のジェネレータのプレースホルダーコンポーネント
function DevGeneratorPlaceholder({ type }: { type: string }) {
  // 実装済みの開発中コンポーネントがあればそれを表示
  const DevComponent = devGeneratorComponents[type]
  if (DevComponent) {
    return <DevComponent />
  }
  const generatorInfo: Record<string, { title: string; description: string; icon: string }> = {
    'society': {
      title: 'ソサエティジェネレータ',
      description: '政府機関から宗教団体、少人数グループまで組織を生成する機能を開発中です',
      icon: '🏛️',
    },
    'building': {
      title: '建造物ジェネレータ',
      description: '城からコンビニまで様々な建築物を詳細に生成します',
      icon: '🏰',
    },
    'dialogue': {
      title: '対話シミュレータ',
      description: 'キャラクター間の自然な会話を生成する機能を開発中です',
      icon: '💬',
    },
    'scene': {
      title: 'シーン描写ジェネレータ',
      description: '物語の場面を詳細に描写する機能を開発中です',
      icon: '🎬',
    },
    'whatif': {
      title: 'What-ifシナリオジェネレータ',
      description: '「もしも」の設定から展開される物語を生成する機能を開発中です',
      icon: '🔄',
    },
    'dungeon': {
      title: 'ダンジョンジェネレータ',
      description: 'RPG用のダンジョン構造を自動生成する機能を開発中です',
      icon: '🗝️',
    },
    'lyric': {
      title: 'リリックジェネレータ',
      description: '様々な要素から歌詞を作成する機能を開発中です',
      icon: '🎵',
    },
    'suno': {
      title: 'Sunoプロンプトジェネレータ',
      description: '音楽生成AI Suno用のプロンプトと歌詞を生成する機能を開発中です',
      icon: '🎼',
    },
  }
  
  const info = generatorInfo[type] || { 
    title: `${type}ジェネレータ`, 
    description: 'このジェネレータは現在開発中です',
    icon: '🚧'
  }
  
  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
        <span className="text-4xl">{info.icon}</span>
        {info.title}
      </h1>
      <div className="card">
        <div className="p-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-orange-600 font-semibold">🚀 開発中の機能</span>
            </div>
            <p className="text-gray-700">{info.description}</p>
          </div>
          
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">テスト用インターフェース</h2>
            <p className="text-sm text-gray-600">
              この画面は開発者モードでのみ表示されています。
              実装が完了次第、正式版として公開予定です。
            </p>
            
            {/* ここに開発中のジェネレータのテストUIを追加 */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <p className="text-gray-500">開発中のUI</p>
              <p className="text-sm text-gray-400 mt-2">ここに新しいジェネレータのコンポーネントを実装</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 開発中のジェネレータのタイプリスト
const devGeneratorTypes = [
  'whatif', 'lyric', 'scenario', 'event', 'textgame'
]

export function GeneratorPage() {
  const { type } = useParams<{ type: string }>()
  const { isDevMode } = useDevModeStore()
  
  // 通常のジェネレータコンポーネントを取得
  const GeneratorComponent = type ? generatorComponents[type] : null
  
  // 開発中のジェネレータへのアクセスチェック
  const isDevGenerator = type && devGeneratorTypes.includes(type)
  
  // 開発中のジェネレータに開発者モード以外でアクセスした場合はリダイレクト
  if (isDevGenerator && !isDevMode) {
    return <Navigate to="/" replace />
  }
  
  // 開発中のジェネレータの場合はプレースホルダーを表示
  if (isDevGenerator) {
    return (
      <GeneratorModelProvider tool={type!}>
        <div className="generator-model-unified">
          <GeneratorModelSelector tool={type!} />
          <DevGeneratorPlaceholder type={type!} />
        </div>
      </GeneratorModelProvider>
    )
  }

  if (!GeneratorComponent) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          {type}ジェネレータ
        </h1>
        <div className="card">
          <p className="text-gray-600">
            このジェネレータは現在開発中です。
          </p>
        </div>
      </div>
    )
  }

  return (
    <GeneratorModelProvider tool={type!}>
      <div className="generator-model-unified">
        <GeneratorModelSelector tool={type!} />
        <GeneratorComponent />
      </div>
    </GeneratorModelProvider>
  )
}
